import React, { FC, useMemo, useState } from 'react';
import CommonLayout from './CommonLayout';
import ButtonPrimary from 'shared/Button/ButtonPrimary';
import { useLoader } from '../../@shared/hooks/useLoader';
import { useAlert } from '../../@shared/hooks/useAlert';
import Avatar from 'shared/Avatar/Avatar';
import { ReviewStatusEnum } from '../../enums/ReviewStatus.enum';
import { useUserSessionStore } from '../../stores/UserSession.store';
import { useReservations } from '../../hooks/collections/useReservations';
import { IReservation } from '../../@core/interfaces/IReservation';

export interface AccountCompanyReviewsProps {
  className?: string;
}

const AccountCompanyReviews: FC<AccountCompanyReviewsProps> = ({ className = '' }) => {
  const user = useUserSessionStore((state) => state.user);
  const loader = useLoader();
  const alert = useAlert();
  const [filter, setFilter] = useState<'pending' | ReviewStatusEnum>('pending');

  const { reservations, updateReservation } = useReservations({
    loadData: true,
    queries: [
      { type: 'where', field: 'service.company.id', operator: '==', value: user?.id },
      { type: 'orderBy', field: 'updatedAt', direction: 'desc' },
    ]
  });

  const orderForReviews = useMemo(() => {
    return filter === 'pending'
      ? reservations.filter(order => !order?.reviewStatus && order?.rating && order?.comment)
      : reservations.filter(order => order?.rating && order?.comment)
  }, [reservations, filter]);

  const handleApproveReview = async (reservationId: string) => {
    loader.start();
    try {
      await updateReservation(reservationId, { reviewStatus: ReviewStatusEnum.ACCEPTED });
      alert.toastSuccess('Review approved successfully');
    } catch (error) {
      alert.toastError('Failed to approve review');
    } finally {
      loader.stop();
    }
  };

  const handleFlagReview = async (reservationId: string) => {
    loader.start();
    try {
      await updateReservation(reservationId, { reviewStatus: ReviewStatusEnum.FLAGGED });
      alert.toastSuccess('Review flagged successfully');
    } catch (error) {
      alert.toastError('Failed to flag review');
    } finally {
      loader.stop();
    }
  };

  const renderReviewItem = (reservation: IReservation) => {
    return (
      <div
        key={reservation.id}
        className="flex flex-col md:flex-row p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg mb-4"
      >
        <div className="flex-shrink-0 flex items-center md:items-start mb-3 md:mb-0 md:mr-4">
          <Avatar userName={reservation.createdBy.name} imgUrl={reservation.createdBy.imageUrl} sizeClass="w-10 h-10"/>
        </div>
        <div className="flex-grow w-full">
          <div className="flex flex-col sm:flex-row justify-between gap-3 mb-2">
            <div className="flex-grow">
              <h3 className="text-base font-medium">{reservation.createdBy.name}</h3>
              <div className="flex flex-wrap items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                <span>{reservation.day}</span>
                <span className="hidden sm:inline">•</span>
                <span>Order: {reservation?.orderId}</span>
                <span className="hidden sm:inline">•</span>
                <div className="flex items-center">
                  {Array(5).fill(0).map((_, i) => (
                    <svg
                      key={i}
                      className={`w-4 h-4 ${i < reservation.rating ? 'text-yellow-500' : 'text-neutral-300 dark:text-neutral-600'}`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                    </svg>
                  ))}
                </div>
              </div>
              <div className="mt-2">
                <span
                  className={`px-2 py-1 rounded-full text-xs ${
                    reservation.reviewStatus === ReviewStatusEnum.ACCEPTED
                      ? 'bg-green-100 text-green-800'
                      : reservation.reviewStatus === ReviewStatusEnum.FLAGGED
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {reservation.reviewStatus === ReviewStatusEnum.ACCEPTED
                    ? 'Approved'
                    : reservation.reviewStatus === ReviewStatusEnum.FLAGGED
                      ? 'Flagged'
                      : 'Pending'}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 sm:flex-nowrap">
              <button
                className="!py-2 !px-3 rounded h-fit text-white text-xs !bg-green-600 hover:!bg-green-700 flex-1 sm:flex-none"
                onClick={() => handleApproveReview(reservation.id)}
              >
                Approve
              </button>
              <button
                className="!py-2 !px-3 text-xs rounded h-fit text-white !bg-yellow-600 hover:!bg-yellow-700 flex-1 sm:flex-none"
                onClick={() => handleFlagReview(reservation.id)}
              >
                Flag
              </button>
            </div>
          </div>
          <p className="mt-3 text-neutral-700 dark:text-neutral-300">{reservation.comment}</p>
          <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
            Service: {reservation.service?.title || 'Unknown Service'}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className={`nc-AccountCompanyReviews ${className}`} data-nc-id="AccountCompanyReviews">
      <CommonLayout>
        <div className="space-y-6 sm:space-y-8">
          <h2 className="text-3xl font-semibold">Manage Reviews</h2>
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

          <div className="flex flex-wrap gap-2 mb-6">
            <ButtonPrimary
              className={`!py-2 !px-4 ${filter === 'pending' ? '' : '!bg-neutral-200 !text-neutral-700 hover:!bg-neutral-300'}`}
              onClick={() => setFilter('pending')}
            >
              Pending reviews
            </ButtonPrimary>
            <ButtonPrimary
              className={`!py-2 !px-4 ${filter === ReviewStatusEnum.ACCEPTED ? '' : '!bg-neutral-200 !text-neutral-700 hover:!bg-neutral-300'}`}
              onClick={() => setFilter(ReviewStatusEnum.ACCEPTED)}
            >
              Approved
            </ButtonPrimary>
            <ButtonPrimary
              className={`!py-2 !px-4 ${filter === ReviewStatusEnum.FLAGGED ? '' : '!bg-neutral-200 !text-neutral-700 hover:!bg-neutral-300'}`}
              onClick={() => setFilter(ReviewStatusEnum.FLAGGED)}
            >
              Flagged
            </ButtonPrimary>
          </div>

          <div className="space-y-6">
            {orderForReviews.length > 0 ? (
              orderForReviews
                .filter(order => filter === 'pending' || order.reviewStatus === filter)
                .map(renderReviewItem)
            ) : (
              <div className="text-center text-neutral-500 dark:text-neutral-400 py-10">
                No reviews to display.
              </div>
            )}
          </div>
        </div>
      </CommonLayout>
    </div>
  );
};

export default AccountCompanyReviews;
